import type { UserType } from "../services/authService";

export const accountProvisioningRoleGroups: {
  label: string;
  roles: [UserType, string][];
}[] = [
  {
    label: "School leadership",
    roles: [
      ["director", "School Director / Owner"],
      ["head_teacher", "Head Teacher / Headmaster"],
      ["director_of_studies", "Director of Studies"],
    ],
  },
  {
    label: "Governance oversight (read-only)",
    roles: [
      ["board_viewer", "Board Chair / Board Viewer"],
      ["donor_viewer", "Donor Viewer"],
    ],
  },
  {
    label: "Teaching team",
    roles: [
      ["head_of_department", "Head of Department"],
      ["class_teacher", "Class Teacher"],
      ["teacher", "Teacher"],
    ],
  },
  {
    // The three roles are one chain: the bursar raises, the principal reviews,
    // the CFO approves. Grouped together and in that order so whoever is
    // setting the school up can see that no one of them works on its own.
    label: "Finance approval chain",
    roles: [
      ["bursar", "Bursar / Accountant — raises transactions"],
      ["principal", "Principal — reviews what the bursar raises"],
      ["cfo", "Chief Finance Officer — approves and posts"],
    ],
  },
  {
    label: "Operations",
    roles: [
      ["librarian", "Librarian"],
      ["inventory_manager", "Inventory Manager"],
      ["transport_manager", "Transport Manager"],
      ["bus_driver", "Bus Driver"],
    ],
  },
  {
    label: "Student services",
    roles: [
      ["school_doctor", "School Doctor"],
      ["school_counsellor", "School Counsellor"],
      ["hostel_matron", "Hostel Matron / Warden"],
      ["hostel_custodian", "Hostel Custodian"],
      ["kitchen_manager", "Kitchen Manager"],
      ["cafeteria_manager", "Cafeteria Manager"],
      ["sports_master", "Sports Master"],
      ["uniform_manager", "Uniform Manager"],
      ["disciplinary_committee", "Disciplinary Committee"],
    ],
  },
];

export const accountRoleLabels = Object.fromEntries(
  accountProvisioningRoleGroups.flatMap((group) => group.roles),
) as Record<string, string>;

export function getProvisionableRoleGroups(actorRole?: UserType) {
  const canProvision = (role: UserType) => {
    if (["board_viewer", "donor_viewer"].includes(role)) {
      return actorRole === "admin" || actorRole === "director";
    }
    // Who signs off on money is an owner's decision. The head teacher leads
    // academics and was deliberately taken out of the finance module, so they
    // do not get to staff the chain that approves it either.
    if (["principal", "cfo"].includes(role)) {
      return actorRole === "admin" || actorRole === "director";
    }
    if (actorRole === "admin") return true;
    if (actorRole === "director") return role !== "director";
    if (actorRole === "head_teacher")
      return !["director", "head_teacher"].includes(role);
    return false;
  };

  return accountProvisioningRoleGroups
    .map((group) => ({
      ...group,
      roles: group.roles.filter(([role]) => canProvision(role)),
    }))
    .filter((group) => group.roles.length);
}
